import { Controller } from "@hotwired/stimulus"
import { TRACKING_CONSENT_ACCEPTED_EVENT, trackingAllowed } from "../lib/analytics"

export default class extends Controller {
  static targets = ["viewsCount", "likesCount", "likeBtn", "shareBtn"]
  static values = { videoId: String }

  connect() {
    this.onTrackingConsentAccepted = this.onTrackingConsentAccepted.bind(this)
    document.addEventListener(TRACKING_CONSENT_ACCEPTED_EVENT, this.onTrackingConsentAccepted)
    if (trackingAllowed()) this.startTracking()
  }

  disconnect() {
    document.removeEventListener(TRACKING_CONSENT_ACCEPTED_EVENT, this.onTrackingConsentAccepted)
  }

  onTrackingConsentAccepted() {
    this.startTracking()
  }

  startTracking() {
    if (this.trackingStarted) return
    this.trackingStarted = true
    this.fingerprint = this.getFingerprint()
    this.recordView()
    this.loadStats()
  }

  getFingerprint() {
    let fp
    try { fp = localStorage.getItem("ford_guia_fp") } catch (_) { /* storage blocked */ }
    if (!fp) {
      fp = this.generateId()
      try { localStorage.setItem("ford_guia_fp", fp) } catch (_) { /* storage blocked */ }
    }
    return fp
  }

  // Safe UUID generator that works in insecure contexts (plain HTTP)
  // where crypto.randomUUID is unavailable.
  generateId() {
    try {
      if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID()
      }
    } catch (_) { /* fall through */ }
    // Fallback: RFC4122-ish v4 from Math.random (not cryptographically strong,
    // but sufficient for anonymous-view deduplication).
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0
      const v = c === "x" ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }

  async recordView() {
    try {
      const resp = await fetch(`/videos/${this.videoIdValue}/view`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fingerprint: this.fingerprint })
      })
      if (resp.ok) {
        const data = await resp.json()
        this.updateViews(data.views_count)
      }
    } catch (e) { /* silent */ }
  }

  async loadStats() {
    try {
      const resp = await fetch(`/videos/${this.videoIdValue}/stats?fingerprint=${this.fingerprint}`)
      if (resp.ok) {
        const data = await resp.json()
        this.updateViews(data.views_count)
        this.updateLikes(data.likes_count, data.liked)
      }
    } catch (e) { /* silent */ }
  }

  async toggleLike() {
    if (!trackingAllowed()) return
    if (!this.fingerprint) this.fingerprint = this.getFingerprint()

    try {
      const resp = await fetch(`/videos/${this.videoIdValue}/like`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fingerprint: this.fingerprint })
      })
      if (resp.ok) {
        const data = await resp.json()
        this.updateLikes(data.likes_count, data.liked)
      }
    } catch (e) { /* silent */ }
  }

  async share() {
    const title = document.title
    const url = window.location.href

    // Try Web Share API (mobile/HTTPS only)
    if (navigator.share) {
      try {
        await navigator.share({ title, url })
        return
      } catch (e) {
        if (e.name === "AbortError") return
        // Fall through to clipboard when native sharing is unavailable after all.
      }
    }

    // Try Clipboard API (HTTPS only)
    let copied = false
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(url)
        copied = true
      } catch (e) { /* fall through */ }
    }

    // Final fallback for plain HTTP: select-and-copy via a temp textarea
    if (!copied) {
      try {
        const ta = document.createElement("textarea")
        ta.value = url
        ta.style.position = "fixed"
        ta.style.opacity = "0"
        document.body.appendChild(ta)
        ta.focus()
        ta.select()
        copied = document.execCommand("copy")
        document.body.removeChild(ta)
      } catch (e) { /* nothing more we can do */ }
    }

    if (this.hasShareBtnTarget) {
      const original = this.shareBtnTarget.innerHTML
      this.shareBtnTarget.innerHTML = copied
        ? `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg> Copiado!`
        : original
      setTimeout(() => { this.shareBtnTarget.innerHTML = original }, 2500)
    }
  }

  updateViews(count) {
    if (this.hasViewsCountTarget) {
      this.viewsCountTarget.textContent = `${this.formatNumber(count)} visualizações`
    }
  }

  updateLikes(count, liked) {
    if (this.hasLikesCountTarget) {
      this.likesCountTarget.textContent = this.formatNumber(count)
    }
    if (this.hasLikeBtnTarget) {
      if (liked) {
        this.likeBtnTarget.classList.add("ford-liked")
      } else {
        this.likeBtnTarget.classList.remove("ford-liked")
      }
    }
  }

  formatNumber(n) {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
    return n.toString()
  }
}
